import { z } from 'zod';
/**
 * Home-screen shortcuts (frictionless flow, piece 1).
 *
 * Three pins on the driver's home — the map's quick-actions stack and
 * the first row of the Menu — defaulted by what the driver is to the
 * network and stored SERVER-SIDE on the User (`pinned_shortcuts`), so
 * the choice survives a reinstall and follows them to a new phone.
 *
 * Persona is derived from signals that already exist, never asked:
 *   homeowner — owns a resident-registered charger (the home)
 *   fleet     — carries a fleet_id
 *   driver    — everyone else
 * Homeowner wins a tie: earning from a charger is the bigger deal than
 * driving for a fleet, and the fleet driver still has Start charger one
 * tap away on the nav.
 *
 * `pinned_shortcuts` null/absent = "use the defaults for my persona",
 * recomputed on every /auth/me so a driver who registers a charger next
 * month gets the homeowner set without touching anything; once they
 * choose their own three, the choice is theirs until they change it.
 */
export const SHORTCUT_IDS = [
    'find_charger',
    'start_charger',
    'start_my_charger',
    'earnings',
    'rewards',
    'friends',
    'my_vehicle',
    'sessions',
    'coupons',
    'favorites',
    'store',
    'wallet',
    'my_home',
    'my_chargers',
    'third_party_chargers',
];
export const ShortcutIdSchema = z.enum(SHORTCUT_IDS);
/**
 * The pins' English names — the PWA's shortcuts.json `items.*` in en,
 * mirrored here so a server-side prompt (EVII's driver profile block)
 * can name what the driver sees on their home screen. Keep in step with
 * apps/web/src/locales/en/shortcuts.json.
 */
export const SHORTCUT_LABELS_EN = {
    find_charger: 'Find charger',
    start_charger: 'Start charger',
    start_my_charger: 'Start my charger',
    earnings: 'Earnings',
    rewards: 'Rewards',
    friends: 'Friends',
    my_vehicle: 'My vehicle',
    sessions: 'Sessions',
    coupons: 'Coupons',
    favorites: 'Favorites',
    store: 'Store',
    wallet: 'Wallet',
    my_home: 'My home',
    my_chargers: 'My chargers',
    third_party_chargers: '3rd-Party Chargers',
};
export const SHORTCUT_PERSONAS = ['homeowner', 'fleet', 'driver'];
export const PINNED_SHORTCUTS_COUNT = 3;
export const SHORTCUT_DEFAULTS = {
    homeowner: ['start_my_charger', 'earnings', 'rewards'],
    driver: ['find_charger', 'rewards', 'friends'],
    fleet: ['start_charger', 'my_vehicle', 'sessions'],
};
/** Exactly three distinct shortcut ids. */
export const PinnedShortcutsSchema = z
    .array(ShortcutIdSchema)
    .length(PINNED_SHORTCUTS_COUNT)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'shortcuts must be distinct' });
/**
 * Persona for the default pins. Homeowner wins on EITHER ownership
 * signal the data has: an active charger the driver owns
 * (Charger.created_by_user_id — stamped by resident-register and by the
 * admin's convert-to-resident, whatever created_via says) or a home of
 * their own (Tenant.owner_user_id, the headless Home tenant My Home
 * creates) even before a charger is registered on it. A charger with
 * NO owner link at all — every admin-CMS, SP-install and mirrored
 * charger until an admin converts it — is invisible to this rule, as
 * it is to every /my-chargers surface; there is nothing in the data
 * to key on. Fleet next, driver otherwise.
 */
export function resolveShortcutPersona(input) {
    if (input.hasHomeCharger || input.hasHome)
        return 'homeowner';
    if (input.fleetId)
        return 'fleet';
    return 'driver';
}
/** The three pins to render: the driver's own choice, else the persona defaults. */
export function effectivePinnedShortcuts(pinned, persona) {
    const parsed = PinnedShortcutsSchema.safeParse(pinned);
    if (parsed.success)
        return parsed.data;
    return SHORTCUT_DEFAULTS[persona];
}
//# sourceMappingURL=shortcuts.js.map